import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { NotificationProvider } from "../contexts/NotificationContext";
import NotificationBell from "./NotificationBell";
import { api } from "../lib/api";
import {
  Layers,
  Home, Users, Store, BarChart3, Brain, ArrowUpRight, LogOut, Receipt, Plus,
  UserPlus, UserCircle, ShieldCheck, Map, QrCode, MessageCircle, RefreshCw,
  Link2, Menu, X, Settings, Clock, Building2, Package, Wallet, CreditCard, Zap,
  Banknote, PiggyBank, Boxes, ShoppingBag, Gauge, UserRoundSearch, UsersRound, Truck,
  ArrowLeftRight, Flame,
} from "lucide-react";

const GOLD = "#F5B400";

const NAV = {
  admin: [
    { to: "/admin", label: "Painel", icon: Home },
    { to: "/admin/clientes", label: "Clientes", icon: Users },
    { to: "/admin/revendedoras", label: "Revendedoras", icon: Store },
    { to: "/admin/oportunidades", label: "Oportunidades", icon: Flame, opp: true },
    { to: "/admin/transferencias", label: "Transferencias", icon: ArrowLeftRight },
    { to: "/admin/produtos", label: "Produtos & Estoque", icon: Package },
    { to: "/admin/limites", label: "Solicitacoes", icon: ArrowUpRight },
    { to: "/admin/credito", label: "Credito Inteligente", icon: Gauge },
    { to: "/admin/credito/radar", label: "Radar de Credito", icon: CreditCard },
    { to: "/admin/crm", label: "CRM Conversas", icon: MessageCircle },
    { to: "/admin/comprovantes", label: "Comprovantes", icon: Receipt },
    { to: "/admin/recuperacao", label: "Recuperacao", icon: RefreshCw },
    { to: "/admin/regua", label: "Regua de Cobranca", icon: Clock },
    { to: "/admin/automacoes", label: "Automacoes WhatsApp", icon: MessageCircle },
    { to: "/admin/pix", label: "PIX Parcelado", icon: QrCode },
    { to: "/admin/central-pagamentos", label: "Central de Pagamentos", icon: Layers },
    { to: "/admin/ia-portal", label: "IA → Portal da Cliente", icon: MessageCircle },
    { to: "/admin/mapa", label: "Mapa de Vendas", icon: Map },
    { to: "/admin/seguro", label: "Seguro Anti-Calote", icon: ShieldCheck },
    { to: "/admin/relatorios", label: "Relatorios", icon: BarChart3 },
    { to: "/admin/ia", label: "IA Interna", icon: Brain },
    { to: "/admin/empresa", label: "Empresa", icon: Building2 },
    { to: "/admin/logistica", label: "CRIS ROTA", icon: Truck },
    { to: "/admin/eletros", label: "Eletros", icon: Zap, eletros: true },
    { to: "/admin/eletros/compradores", label: "Compradores Eletros", icon: UserRoundSearch, eletros: true },
    { to: "/admin/eletros/catalogo", label: "Catalogo Eletros", icon: Package, eletros: true },
    { to: "/admin/eletros/recebimentos", label: "Recebimentos Eletros", icon: Wallet, eletros: true },
    { to: "/admin/eletros/comissoes", label: "Comissoes Eletros", icon: CreditCard, eletros: true },
    { to: "/admin/eletros/saques", label: "Saques Eletros", icon: Banknote, eletros: true },
    { to: "/admin/eletros/estoque", label: "Estoque Eletros", icon: Boxes, eletros: true },
    { to: "/admin/eletros/relatorios", label: "Relatorios Eletros", icon: BarChart3, eletros: true },
    { to: "/admin/configuracoes", label: "Configuracoes", icon: Settings },
  ],
  reseller: [
    { to: "/revenda", label: "Painel", icon: Home },
    { to: "/revenda/clientes", label: "Minha Carteira", icon: Users },
    { to: "/revenda/transferencias", label: "Transferencias", icon: ArrowLeftRight },
    { to: "/revenda/oportunidades", label: "Oportunidades", icon: Flame, opp: true },
    { to: "/revenda/credito/radar", label: "Radar de Credito", icon: Gauge },
    { to: "/revenda/clientes/novo", label: "Novo Cliente", icon: UserPlus },
    { to: "/revenda/venda/nova", label: "Nova Venda", icon: Plus },
    { to: "/revenda/afiliacao", label: "Meu Link", icon: Link2 },
    { to: "/revenda/eletros", label: "Eletros", icon: Zap, eletros: true },
    { to: "/revenda/eletros/compradores", label: "Meus Compradores", icon: UsersRound, eletros: true },
    { to: "/revenda/eletros/conta", label: "Conta Corrente", icon: PiggyBank, eletros: true },
    { to: "/revenda/crm", label: "CRM Conversas", icon: MessageCircle },
    { to: "/revenda/comprovantes", label: "Comprovantes", icon: Receipt },
    { to: "/revenda/recuperacao", label: "Recuperacao", icon: RefreshCw },
    { to: "/revenda/perfil", label: "Meu Perfil", icon: UserCircle },
    { to: "/revenda/configuracoes", label: "Configuracoes", icon: Settings },
  ],
  client: [
    { to: "/cliente", label: "Início", icon: Home },
    { to: "/cliente/parcelas", label: "Minhas Parcelas", icon: Receipt },
  ],
};

// Itens administrativos operacionais do Eletros (para o Administrador Eletros / admin principal).
// NAO inclui a aba "/admin/eletros" (Configuracoes/Eletro) - exclusiva do admin principal.
const ELETROS_ADMIN_NAV = [
  { to: "/admin/eletros/compradores", label: "Compradores Eletros", icon: UserRoundSearch, eletros: true },
  { to: "/admin/eletros/relatorios", label: "Dashboard Eletros", icon: BarChart3, eletros: true },
  { to: "/admin/eletros/catalogo", label: "Catalogo Eletros", icon: Package, eletros: true },
  { to: "/admin/eletros/estoque", label: "Estoque Eletros", icon: Boxes, eletros: true },
  { to: "/admin/eletros/recebimentos", label: "Recebimentos Eletros", icon: Wallet, eletros: true },
  { to: "/admin/eletros/comissoes", label: "Comissoes Eletros", icon: CreditCard, eletros: true },
  { to: "/admin/eletros/saques", label: "Saques Eletros", icon: Banknote, eletros: true },
];

// Atalhos do menu inferior (mobile). O menu completo continua no drawer.
const MOBILE_NAV = {
  admin: [
    { to: "/admin", label: "Início", icon: Home, end: true },
    { to: "/admin/clientes", label: "Clientes", icon: Users },
    { to: "/admin/revendedoras", label: "Revendas", icon: Store },
    { to: "/admin/comprovantes", label: "Vendas", icon: Wallet },
    { to: "/admin/recuperacao", label: "Cobranças", icon: CreditCard },
  ],
  reseller: [
    { to: "/revenda", label: "Início", icon: Home, end: true },
    { to: "/revenda/clientes", label: "Clientes", icon: Users },
    { to: "/revenda/comprovantes", label: "Vendas", icon: Wallet },
    { to: "/revenda/recuperacao", label: "Cobranças", icon: CreditCard },
    { to: "/revenda/perfil", label: "Perfil", icon: UserCircle },
  ],
  client: [
    { to: "/cliente", label: "Início", icon: Home, end: true },
    { to: "/cliente/parcelas", label: "Parcelas", icon: Receipt },
  ],
};

const FAB = {
  reseller: { to: "/revenda/venda/nova", label: "Nova Venda" },
};

// Barra inferior (mobile) contextual quando o usuario esta no modulo Eletros.
const MOBILE_NAV_ELETROS = {
  admin: [
    { to: "/admin", label: "Início", icon: Home, end: true },
    { to: "/admin/eletros", label: "Eletros", icon: Zap, end: true, eletros: true },
    { to: "/admin/eletros/compradores", label: "Compradores", icon: UsersRound, eletros: true },
    { to: "/admin/eletros/recebimentos", label: "Receb.", icon: Wallet, eletros: true },
    { to: "/admin/eletros/comissoes", label: "Comissões", icon: CreditCard, eletros: true },
  ],
  reseller: [
    { to: "/revenda", label: "Início", icon: Home, end: true },
    { to: "/revenda/eletros", label: "Eletros", icon: Zap, end: true, eletros: true },
    { to: "/revenda/eletros/compradores", label: "Compradores", icon: UsersRound, eletros: true },
    { to: "/revenda/eletros/conta", label: "Conta", icon: PiggyBank, eletros: true },
    { to: "/revenda/perfil", label: "Perfil", icon: UserCircle },
  ],
};

function BrandLogo({ variant = "compact" }) {
  if (variant === "full") {
    return (
      <img
        src="/logo-full.png"
        alt="Banco das Revendedoras - Cris Lingerie"
        className="mx-auto w-full max-w-[168px] rounded-2xl shadow-card"
      />
    );
  }
  return (
    <div className="flex items-center gap-3">
      <img
        src="/icon-192x192.png"
        alt="BR"
        className="h-10 w-10 shrink-0 rounded-xl shadow-[0_6px_16px_rgba(106,27,154,0.35)]"
      />
      <div className="leading-tight">
        <div className="text-[15px] font-semibold text-brand-dark">Cris Lingerie</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-brand">Banco das Revendedoras</div>
      </div>
    </div>
  );
}

function NavItems({ items, onNavigate, idPrefix = "" }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            to={it.to}
            key={it.to}
            end
            onClick={onNavigate}
            data-testid={`${idPrefix}nav-${it.to.replaceAll("/", "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? "bg-brand-grad text-white shadow-[0_6px_18px_rgba(106,27,154,0.28)]"
                  : "text-[#6B5A69] hover:bg-muted hover:text-brand-dark"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={19} strokeWidth={2} style={it.eletros ? { color: isActive ? GOLD : "#C9960A" } : undefined} />
                <span className="truncate font-semibold" style={it.eletros ? { color: isActive ? GOLD : "#C9960A" } : undefined}>{it.label}</span>
                {it.badge > 0 ? (
                  <span
                    data-testid={`${idPrefix}nav-badge-${it.to.replaceAll("/", "-")}`}
                    className={`ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
                      isActive ? "bg-white text-brand" : "bg-brand text-white"
                    }`}
                  >
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function VersionFooter() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/version").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  if (!info) return null;
  const build = info.build_time ? new Date(info.build_time).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const ok = info.status === "ok";
  return (
    <div className="mt-3 px-1 text-[10px] leading-tight text-[#B4A6B2]" data-testid="app-version-footer" title={`${info.environment} · ${info.db}`}>
      <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${ok ? "bg-emerald-400" : "bg-red-400"}`} />
      Versão: {info.git_sha} • Build: {build}
    </div>
  );
}

function ProfileBlock({ user, onLogout, idPrefix = "" }) {
  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="mb-3 flex items-center gap-3 px-1">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-grad font-semibold text-white">
          {(user.name || "?")[0]?.toUpperCase()}
        </div>
        <div className="overflow-hidden">
          <div className="truncate text-sm font-medium text-brand-dark">{user.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-brand">{user.role}</div>
        </div>
      </div>
      <button
        onClick={onLogout}
        data-testid={`${idPrefix}logout-button`}
        className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#6B5A69] transition-colors hover:bg-muted hover:text-brand-dark"
      >
        <LogOut size={19} />
        <span>Sair</span>
      </button>
      <VersionFooter />
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const [eletrosOn, setEletrosOn] = useState(false);
  const [eletrosAdmin, setEletrosAdmin] = useState(false);
  const [oppOn, setOppOn] = useState(false);
  const [oppNew, setOppNew] = useState(0);
  const [oppAdminPending, setOppAdminPending] = useState(0);
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") { setEletrosOn(true); setEletrosAdmin(true); return; }
    if (user.role === "reseller") {
      api.get("/eletros/me/access").then((r) => {
        setEletrosOn(!!r.data?.enabled);
        setEletrosAdmin(!!r.data?.eletros_admin);
      }).catch(() => { setEletrosOn(false); setEletrosAdmin(false); });
    }
  }, [user]);

  // OP-5: feature flag do modulo de Oportunidades + badge de NOVAS (estado real do backend).
  useEffect(() => {
    if (!user || !["admin", "reseller"].includes(user.role)) return;
    let alive = true;
    const fetchAccess = () => {
      api.get("/opportunities/access").then((r) => {
        if (!alive) return;
        setOppOn(!!r.data?.can_view);
        setOppNew(Number(r.data?.new_count) || 0);
        setOppAdminPending(Number(r.data?.admin_pending) || 0);
      }).catch(() => { if (alive) { setOppOn(false); setOppNew(0); setOppAdminPending(0); } });
    };
    fetchAccess();
    const t = setInterval(fetchAccess, 60000); // refresh leve a cada 60s
    return () => { alive = false; clearInterval(t); };
  }, [user, location.pathname]);

  if (!user) return null;
  let items = (NAV[user.role] || []).filter((it) => (!it.eletros || eletrosOn) && (!it.opp || oppOn));
  items = items.map((it) => (it.opp ? { ...it, badge: user.role === "admin" ? oppAdminPending : oppNew } : it));
  // Administrador Eletros (reseller com eletros_admin): ganha o menu operacional admin do nicho.
  if (user.role === "reseller" && eletrosAdmin) {
    items = [...items, ...ELETROS_ADMIN_NAV];
  }
  const onEletros = location.pathname.startsWith("/revenda/eletros") || location.pathname.startsWith("/admin/eletros");
  const mobileItems = (onEletros && eletrosOn && MOBILE_NAV_ELETROS[user.role]) || MOBILE_NAV[user.role] || [];
  let fab = FAB[user.role];
  if (user.role === "reseller" && onEletros && eletrosOn) {
    fab = { to: "/revenda/eletros/venda", label: "Nova Venda Eletros", eletros: true };
  }
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <NotificationProvider>
    <div className="min-h-screen bg-background">
      {/* ===== Desktop sidebar ===== */}
      <aside
        className="fixed left-0 top-0 z-30 hidden h-screen w-72 flex-col border-r border-border bg-white px-4 py-6 lg:flex"
        data-testid="app-sidebar"
      >
        <div className="flex items-start justify-between px-2">
          <BrandLogo variant="full" />
          <NotificationBell idPrefix="desktop-" />
        </div>
        <div className="mt-8 flex-1 overflow-y-auto pr-1">
          <NavItems items={items} />
        </div>
        <ProfileBlock user={user} onLogout={doLogout} />
      </aside>

      {/* ===== Mobile top header ===== */}
      <header
        className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-white/85 px-4 pb-3 backdrop-blur-md lg:hidden"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.65rem)" }}
        data-testid="mobile-header"
      >
        <BrandLogo />
        <div className="flex items-center gap-1">
          <NotificationBell idPrefix="mobile-" />
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            data-testid="open-sidebar-btn"
            className="relative z-10 grid h-11 w-11 shrink-0 cursor-pointer touch-manipulation place-items-center rounded-xl text-brand-dark transition-colors hover:bg-muted active:bg-muted"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* ===== Mobile full-menu drawer ===== */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-brand-dark/50 lg:hidden"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              data-testid="sidebar-backdrop"
            />
            <motion.aside
              className="fixed left-0 top-0 z-50 flex h-screen w-[84vw] max-w-[320px] flex-col bg-white px-4 pb-6 shadow-pop lg:hidden"
              style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              data-testid="mobile-drawer"
            >
              <div className="mb-6 flex items-center justify-between">
                <BrandLogo />
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Fechar menu"
                  data-testid="close-sidebar-btn"
                  className="grid h-11 w-11 shrink-0 touch-manipulation place-items-center rounded-xl text-brand-dark hover:bg-muted active:bg-muted"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto pr-1">
                <NavItems items={items} onNavigate={() => setMenuOpen(false)} idPrefix="drawer-" />
              </div>
              <ProfileBlock user={user} onLogout={doLogout} idPrefix="drawer-" />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ===== Main content ===== */}
      <main className="px-4 py-6 pb-[calc(7rem+env(safe-area-inset-bottom))] sm:px-6 lg:ml-72 lg:px-10 lg:py-8 lg:pb-10" data-testid="main-content">
        <Outlet />
      </main>

      {/* ===== Mobile bottom nav ===== */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 rounded-t-2xl border-t border-border bg-white/85 backdrop-blur-md lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)", boxShadow: "0 -6px 24px rgba(43,13,46,0.09)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="mx-auto flex max-w-md items-stretch justify-around px-1">
          {mobileItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`bottomnav-${it.to.replaceAll("/", "-")}`}
                className={({ isActive }) =>
                  `flex min-h-[56px] flex-1 touch-manipulation flex-col items-center justify-center gap-1 py-1.5 text-[10px] transition-colors ${
                    isActive ? "text-brand" : "text-[#9A8A98]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`grid h-8 w-10 place-items-center rounded-full transition-all duration-200 ${isActive ? "-translate-y-0.5 bg-brand/10" : ""}`} style={it.eletros && isActive ? { background: "rgba(245,180,0,0.16)" } : undefined}>
                      <Icon size={isActive ? 23 : 21} strokeWidth={isActive ? 2.5 : 2} style={it.eletros ? { color: isActive ? "#C9960A" : "#B79256" } : undefined} />
                    </span>
                    <span className={isActive ? "font-semibold" : "font-medium"} style={it.eletros ? { color: isActive ? "#C9960A" : "#B79256" } : undefined}>{it.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* ===== FAB (Nova Venda) ===== */}
      {fab && (
        <button
          type="button"
          onClick={() => nav(fab.to)}
          data-testid={fab.eletros ? "fab-new-sale-eletros" : "fab-new-sale"}
          aria-label={fab.label}
          className={`fixed right-4 z-30 grid h-14 w-14 touch-manipulation place-items-center rounded-full transition-transform duration-200 hover:scale-105 active:scale-90 lg:hidden ${
            fab.eletros
              ? "text-[#2B1E4A] shadow-[0_10px_28px_rgba(245,180,0,0.5)]"
              : "bg-brand-grad text-white shadow-[0_10px_28px_rgba(106,27,154,0.45)]"
          }`}
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 5.25rem)", ...(fab.eletros ? { background: GOLD } : {}) }}
        >
          {fab.eletros ? <ShoppingBag size={25} strokeWidth={2.4} /> : <Plus size={26} strokeWidth={2.5} />}
        </button>
      )}
    </div>
    </NotificationProvider>
  );
}
